import { showUpgradeNotice } from "@/lib/planGate";
import axios from "axios";
import { handleUnauthorized } from "@/lib/silentSso";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Gateway token handed back by the central login app (see SsoCallback.jsx);
// hikmah-backend's proxy only accepts it as a Bearer header.
export const tokenStore = {
  get: () => { try { return localStorage.getItem("hwh_token"); } catch { return null; } },
  set: (t) => { try { localStorage.setItem("hwh_token", t); } catch { /* private mode */ } },
  clear: () => { try { localStorage.removeItem("hwh_token"); } catch { /* private mode */ } },
};
api.interceptors.request.use((config) => {
  const t = tokenStore.get();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  // Long-form content (tafsir, tadabbur, the Memorize "Understand" excerpt) is
  // translated server-side; it needs to know the language the user picked.
  // Read at request time so a language switch applies to the very next call.
  try {
    const lang = window.localStorage.getItem("hwh_lang");
    if (lang && lang !== "en") config.headers["X-Content-Lang"] = lang;
  } catch { /* storage blocked: stay English */ }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 402) showUpgradeNotice(typeof err.response.data?.detail === "object" ? err.response.data.detail : {});
    if (err.response?.status === 401) {
      handleUnauthorized({ url: err.config?.url, method: err.config?.method, hadToken: !!tokenStore.get(),
        setToken: tokenStore.set, clearToken: tokenStore.clear });
    }
    return Promise.reject(err);
  },
);

export const recordVisit = (payload) => api.post("/activity/visit", payload).catch(() => {});

// Ends the gateway session cookie too (single sign-on) — otherwise the common
// login page would silently sign the user straight back in.
export const gatewayLogout = () =>
  fetch(`${process.env.REACT_APP_GATEWAY_URL}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});

// Sign-out from this app returns to the dashboard's Apps page (same origin as the common login page). The
// gateway session is deliberately kept alive because that page needs it; the dashboard's own "Sign out"
// ends it for every app.
const LOGIN_URL = process.env.REACT_APP_LOGIN_URL || "http://localhost:3007/login";
export const leaveToDashboard = () => {
  tokenStore.clear();
  window.location.replace(`${new URL(LOGIN_URL).origin}/dashboard?page=apps`);
};


// Several pages (recitation upload, voice notes, CSV import...) call fetch()
// directly instead of the axios client above, so they never got the Bearer
// token the gateway requires on POST/PUT/DELETE. Attach it here, once, to every
// fetch that targets the gateway — callers' own Authorization still wins.
export function installAuthFetch() {
  const base = process.env.REACT_APP_BACKEND_URL;
  const gateway = process.env.REACT_APP_GATEWAY_URL;
  if (!base || window.__hvAuthFetch) return;
  window.__hvAuthFetch = true;
  const original = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : "";
    const token = tokenStore.get();
    if (token && url && (url.startsWith(base) || (gateway && url.startsWith(gateway)))) {
      const headers = new Headers(init.headers);
      if (!headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
      return original(input, { ...init, headers }).then((res) => {
        if (res.status === 402) res.clone().json().then((b) => showUpgradeNotice(typeof b?.detail === "object" ? b.detail : {})).catch(() => showUpgradeNotice({}));
        if (res.status === 401) {
          handleUnauthorized({ url, method: init.method || "GET", hadToken: true,
            setToken: tokenStore.set, clearToken: tokenStore.clear });
        }
        return res;
      });
    }
    return original(input, init);
  };
}
